<script setup>
import { Head, Link, useForm } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import Checkbox from "primevue/checkbox";
import Message from "primevue/message";
import InputNumber from "primevue/inputnumber";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { computed, ref, watch } from "vue";
import Chips from "primevue/chips";
import { useToast } from "primevue/usetoast";
import axios from "axios";
import ImageInput from "../../../Components/Management/ImageInput.vue";

const props = defineProps([
    'companies',
    'categories',
    'providers',
    'devices',
    'flash',
]);

const route = window.route;
const toast = useToast();
const errors = ref({});
const filteredProviders = ref(props.providers);
const filteredDevices = ref(props.devices);

const serviceTypeOptions = [
    { label: 'Self Bookable', value: 'self_bookable' },
    { label: 'Staff Assisted', value: 'staff_assisted' },
    { label: 'Display Only', value: 'display_only' },
    { label: 'Virtual Provider Based', value: 'virtual_provider_based' },
];

const resourceTypeOptions = [
    { label: 'Kein Filter (Alle)', value: null },
    { label: 'Frauen', value: 'female' },
    { label: 'Männer', value: 'male' },
];

const form = useForm({
    company_id: null,
    service_category_id: null,
    service_type: null,
    resource_type_filter: null,
    name: '',
    description: '',
    duration: 30,
    buffer_before_minutes: 0,
    buffer_after_minutes: 0,
    price: 0,
    is_active: true,
    visible_in_app: true,
    bookable_by_customer: true,
    cancelable_by_customer: true,
    requires_manual_confirmation: false,
    allow_extra_minutes: false,
    extra_minutes_options: [],
    sort_order: 0,
    notes_internal: '',
    providers: [], // Array of provider IDs
    device_requirements: [],
    image: null,
});

// Update providers list when company changes
watch(() => form.company_id, async (newId) => {
    if (newId) {
        try {
            const { data } = await axios.get(`/ajax/admin/providers?company_id=${newId}&all=1`);
            filteredProviders.value = data.data || data; // Handle both paginated and non-paginated
        } catch (e) {
            console.error("Failed to fetch providers for company", e);
            filteredProviders.value = [];
        }
    } else {
        filteredProviders.value = props.providers;
        filteredDevices.value = props.devices;
    }
});

watch(() => form.company_id, (newId) => {
    if (newId) {
        filteredDevices.value = props.devices.filter((device) => device.company_id === newId);
    } else {
        filteredDevices.value = props.devices;
    }
}, { immediate: true });

const addDeviceRequirement = () => {
    form.device_requirements.push({
        device_id: null,
        is_required: true,
        quantity: 1,
    });
};

const removeDeviceRequirement = (index) => {
    form.device_requirements.splice(index, 1);
};

const availableDevicesForRow = (rowIndex) => {
    const currentId = form.device_requirements[rowIndex]?.device_id;

    return filteredDevices.value.filter((device) => {
        if (device.id === currentId) {
            return true;
        }

        return !form.device_requirements.some((row, index) => index !== rowIndex && row.device_id === device.id);
    });
};

const submit = () => {
    if (form.buffer_before_minutes === null) form.buffer_before_minutes = 0;
    if (form.buffer_after_minutes === null) form.buffer_after_minutes = 0;
    if (form.price === null) form.price = 0;

    form.post('/management/services', {
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Erfolgreich',
                detail: 'Dienstleistung wurde erfolgreich gespeichert.',
                life: 5000,
            });
        },
        onError: (err) => {
            errors.value = err;
            toast.add({
                severity: 'error',
                summary: 'Fehler beim Speichern',
                detail: 'Bitte prüfen Sie die Eingaben.',
                life: 5000,
            });
        }
    });
};

const hasErrors = computed(() => Object.values(errors.value).some(v => v));

</script>

<template>
    <Layout :flash="flash">
        <Head title="Dienstleistung erstellen" />

        <div class="text-sm">
            <h1 class="text-2xl font-bold mb-8 text-gray-800">Neue Dienstleistung erstellen</h1>

            <Message v-if="hasErrors" severity="error" class="mb-6">
                <template v-for="(error, key) in errors" :key="key">
                    <div v-if="error">{{ Array.isArray(error) ? error[0] : error }}</div>
                </template>
            </Message>

            <form @submit.prevent="submit" class="space-y-8">
                <!-- Section 1: Basic Settings -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">1</div>
                        <h2 class="text-lg font-bold text-gray-700">Grundlagen</h2>
                    </div>
                    
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        <div v-if="$page.props.auth.user.role === 'super_admin'" class="flex flex-col gap-2">
                            <label for="company_id" class="font-bold text-gray-600">Unternehmen *</label>
                            <Dropdown id="company_id" v-model="form.company_id" :options="companies" optionLabel="name" optionValue="id" 
                                      placeholder="Unternehmen wählen" :class="{'p-invalid': errors.company_id}" filter class="w-full"
                                      @change="errors.company_id = null" />
                            <small v-if="errors.company_id" class="p-error">{{ errors.company_id }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="service_category_id" class="font-bold text-gray-600">Kategorie *</label>
                            <Dropdown id="service_category_id" v-model="form.service_category_id" :options="categories" optionLabel="name" optionValue="id" 
                                      placeholder="Kategorie wählen" filter class="w-full" :class="{'p-invalid': errors.service_category_id}" @change="errors.service_category_id = null" />
                            <small v-if="errors.service_category_id" class="p-error">{{ errors.service_category_id }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="service_type" class="font-bold text-gray-600">Service Typ</label>
                            <Dropdown id="service_type" v-model="form.service_type" :options="serviceTypeOptions" optionLabel="label" optionValue="value" placeholder="Typ wählen" class="w-full" />
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="resource_type_filter" class="font-bold text-gray-600">Ressourcen-Typ Filter</label>
                            <Dropdown id="resource_type_filter" v-model="form.resource_type_filter" :options="resourceTypeOptions" optionLabel="label" optionValue="value" placeholder="Filter wählen" class="w-full" />
                        </div>

                        <div class="flex flex-col gap-2 md:col-span-2">
                            <label for="name" class="font-bold text-gray-600">Name der Dienstleistung *</label>
                            <InputText id="name" v-model="form.name" :class="{'p-invalid': errors.name}" class="w-full" placeholder="z.B. Ganzkörperbehandlung" 
                                       @input="errors.name = null" />
                            <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
                        </div>

                        <div class="flex flex-col gap-2 md:col-span-2">
                            <label for="description" class="font-bold text-gray-600">Beschreibung</label>
                            <Textarea id="description" v-model="form.description" rows="3" class="w-full" />
                        </div>

                        <div class="flex flex-col gap-2 md:col-span-2">
                            <ImageInput v-model="form.image" label="Bild" :error="errors.image" />
                        </div>
                    </div>
                </div>

                <!-- Section 2: Pricing & Timing -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">2</div>
                        <h2 class="text-lg font-bold text-gray-700">Preis & Dauer</h2>
                    </div>
                    
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        <div class="flex flex-col gap-2">
                            <label for="duration" class="font-bold text-gray-600">Dauer (Minuten) *</label>
                            <InputNumber id="duration" v-model="form.duration" :min="1" class="w-full" inputClass="w-full" showButtons :class="{'p-invalid': errors.duration}" @input="errors.duration = null" />
                            <small v-if="errors.duration" class="p-error">{{ errors.duration }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="price" class="font-bold text-gray-600">Preis (CHF)</label>
                            <InputNumber id="price" v-model="form.price" mode="currency" currency="CHF" locale="de-CH" class="w-full" inputClass="w-full" />
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="buffer_before" class="font-bold text-gray-600">Puffer davor (Min)</label>
                            <InputNumber id="buffer_before" v-model="form.buffer_before_minutes" :min="0" class="w-full" inputClass="w-full" />
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="buffer_after" class="font-bold text-gray-600">Puffer danach (Min)</label>
                            <InputNumber id="buffer_after" v-model="form.buffer_after_minutes" :min="0" class="w-full" inputClass="w-full" />
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="allow_extra_minutes" class="font-bold text-gray-600">Extrazeit erlauben</label>
                            <div class="flex items-center gap-2 mt-1">
                                <Checkbox id="allow_extra_minutes" v-model="form.allow_extra_minutes" :binary="true" />
                                <label for="allow_extra_minutes" class="text-gray-600 text-sm cursor-pointer">Kunden können bei der Buchung Extrazeit hinzufügen</label>
                            </div>
                        </div>

                        <div class="flex flex-col gap-2" v-if="form.allow_extra_minutes">
                            <label for="extra_minutes_options" class="font-bold text-gray-600">Extrazeit-Optionen (Min)</label>
                            <Chips id="extra_minutes_options" v-model="form.extra_minutes_options" separator="," class="w-full" placeholder="z.B. 15, 30, 60" />
                            <small class="text-gray-400">Kommagetrennte Minutenwerte eingeben und Enter drücken</small>
                        </div>
                    </div>
                </div>

                <!-- Section 3: Assignment -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">3</div>
                        <h2 class="text-lg font-bold text-gray-700">Zuweisung</h2>
                    </div>
                    
                    <div class="flex flex-col gap-2">
                        <label for="providers" class="font-bold text-gray-600">Mitarbeiter / Ressourcen für diese Dienstleistung</label>
                        <MultiSelect id="providers" v-model="form.providers" :options="filteredProviders" optionLabel="name" optionValue="id" 
                                     placeholder="Mitarbeiter auswählen" filter class="w-full" />
                        <p class="text-xs text-gray-400 mt-1" v-if="!form.company_id">Bitte wählen Sie zuerst ein Unternehmen aus.</p>
                    </div>

                    <div class="mt-8">
                        <div class="flex items-center justify-between mb-4">
                            <div>
                                <h3 class="text-base font-bold text-gray-700">Geräteanforderungen</h3>
                                <p class="text-xs text-gray-400">Welche Geräte und Mengen diese Dienstleistung benötigt.</p>
                            </div>
                            <Button type="button" @click="addDeviceRequirement" label="Anforderung hinzufügen" icon="pi pi-plus" size="small" severity="secondary" outlined />
                        </div>

                        <DataTable :value="form.device_requirements" class="p-datatable-sm" responsiveLayout="scroll">
                            <Column header="Gerät *">
                                <template #body="{ data, index }">
                                    <Dropdown v-model="data.device_id" :options="availableDevicesForRow(index)" optionLabel="name" optionValue="id" placeholder="Gerät wählen" class="w-full p-inputtext-sm" filter />
                                </template>
                            </Column>
                            <Column header="Pflicht" headerStyle="width: 6rem">
                                <template #body="{ data }">
                                    <Checkbox v-model="data.is_required" :binary="true" />
                                </template>
                            </Column>
                            <Column header="Menge" headerStyle="width: 8rem">
                                <template #body="{ data }">
                                    <InputNumber v-model="data.quantity" :min="1" class="w-full" inputClass="w-full p-inputtext-sm" showButtons />
                                </template>
                            </Column>
                            <Column headerStyle="width: 3rem">
                                <template #body="{ index }">
                                    <Button icon="pi pi-times" severity="danger" text rounded @click="removeDeviceRequirement(index)" />
                                </template>
                            </Column>
                        </DataTable>

                        <div v-if="form.device_requirements.length === 0" class="text-center py-8 bg-white rounded-lg border border-dashed border-gray-200 mt-2">
                            <p class="text-gray-400">Keine Geräteanforderungen definiert.</p>
                        </div>
                    </div>
                </div>

                <!-- Section 4: Permissions -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">4</div>
                        <h2 class="text-lg font-bold text-gray-700">Sichtbarkeit & Buchung</h2>
                    </div>
                    
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                            <Checkbox id="is_active" v-model="form.is_active" :binary="true" />
                            <label for="is_active" class="font-bold text-gray-700 cursor-pointer">Aktiv</label>
                        </div>
                        <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                            <Checkbox id="visible_in_app" v-model="form.visible_in_app" :binary="true" />
                            <label for="visible_in_app" class="font-bold text-gray-700 cursor-pointer">Sichtbar in App</label>
                        </div>
                        <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                            <Checkbox id="bookable_by_customer" v-model="form.bookable_by_customer" :binary="true" />
                            <label for="bookable_by_customer" class="font-bold text-gray-700 cursor-pointer">Buchbar durch Kunden</label>
                        </div>
                        <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                            <Checkbox id="cancelable_by_customer" v-model="form.cancelable_by_customer" :binary="true" />
                            <label for="cancelable_by_customer" class="font-bold text-gray-700 cursor-pointer">Stornierbar durch Kunden</label>
                        </div>
                        <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                            <Checkbox id="requires_manual_confirmation" v-model="form.requires_manual_confirmation" :binary="true" />
                            <label for="requires_manual_confirmation" class="font-bold text-gray-700 cursor-pointer">Manuelle Bestätigung erforderlich</label>
                        </div>
                    </div>
                </div>

                <!-- Section 5: Advanced -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">5</div>
                        <h2 class="text-lg font-bold text-gray-700">Erweitert</h2>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        <div class="flex flex-col gap-2">
                            <label for="sort_order" class="font-bold text-gray-600">Sortierreihenfolge</label>
                            <InputNumber id="sort_order" v-model="form.sort_order" :min="0" class="w-full" inputClass="w-full" showButtons />
                            <small class="text-gray-400">Niedrigere Werte werden zuerst angezeigt.</small>
                        </div>

                        <div class="flex flex-col gap-2 md:col-span-2">
                            <label for="notes_internal" class="font-bold text-gray-600">Interne Notizen</label>
                            <Textarea id="notes_internal" v-model="form.notes_internal" rows="4" class="w-full" placeholder="Nur intern sichtbar – z.B. besondere Hinweise für Mitarbeiter" />
                        </div>
                    </div>
                </div>

                <!-- Footer Actions -->
                <div class="flex justify-between items-center pt-8 border-t mt-10">
                    <Button asChild v-slot="slotProps" severity="secondary" outlined>
                        <Link href="/management/services" :class="slotProps.class" class="px-8 py-3 rounded-xl border-gray-300 font-bold">
                            <i class="pi pi-arrow-left mr-2" />
                            Abbrechen
                        </Link>
                    </Button>

                    <Button type="submit" 
                            class="!bg-black !text-[#FFF053] !border-none px-10 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-gray-200" 
                            :loading="form.processing" :disabled="form.processing">
                        <i class="pi pi-save" />
                        Dienstleistung speichern
                    </Button>
                </div>
            </form>
        </div>
    </Layout>
</template>
